import { cn } from 'util/cn'
import generateAvatarColors, { stringAsciiPRNG } from 'util/color_generator'
import { getInitials } from 'util/get_initials'

import { css } from 'design'
import { useComponentId } from 'hooks/use_component_id'
import { Badge } from 'primitives/badge'
import React, { useLayoutEffect, useMemo, useState } from 'react'

import { useHandleMetadata } from '../../hooks/use_handle_metadata'

export enum AvatarSizes {
  SMALL = 22,
  MEDIUM = 42,
  LARGE = 48,
  HUGE = 58,
  MASSIVE = 80,
}

export type AvatarProps = {
  handle?: string
  size?: AvatarSizes
  imageSize?: AvatarSizes
  hideOnlineIndicator: boolean
  presence: 'offline' | 'away' | 'online'
  isActiveMember: boolean
  badge?: number
}

type PotentialString = string | undefined

const imageStyles = css({
  borderRadius: '20%',
})

const avatarStyles = css({
  display: 'inline-flex',
  position: 'relative',
  userSelect: 'none',
})

const avatarImageStyles = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    isActiveMember: {
      true: {
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '100%',
          height: '100%',
          boxShadow: 'inset 0 0 $space$1 $colors$grey100',
          opacity: 0.4,
          borderRadius: '$2',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          zIndex: 1,
        },
      },
    },
  },
})

export const Avatar = ({
  handle,
  size = AvatarSizes['MEDIUM'],
  imageSize,
  hideOnlineIndicator,
  presence,
  isActiveMember,
  badge,
  className,
  ...props
}: AvatarProps & React.HTMLAttributes<HTMLDivElement>) => {
  const [initials, setInitials] = useState<string>('')
  const [profileImage, setProfileImage] = useState<PotentialString>(undefined)

  const iconColors = generateAvatarColors(handle)
  const svgSize = 240
  const viewBoxSize = 50 + stringAsciiPRNG(handle, 50)
  const xOffset = stringAsciiPRNG(handle, svgSize - viewBoxSize)
  const yOffset = stringAsciiPRNG(
    handle + viewBoxSize.toString(),
    svgSize - viewBoxSize,
  )
  const gradStartPercent = (xOffset + yOffset) / 2
  const gradAngle = stringAsciiPRNG(handle, 45)
  const { publicContact, name } = useHandleMetadata(handle)

  const weakUid = `ucid-${useComponentId()}`

  useLayoutEffect(() => {
    if (!name) return

    // set initials by trying for
    // -- (1) public contact
    // -- (2) then contact account_name
    // -- (3) then contact name
    // -- (4) then handle
    setInitials(getInitials(name))
    setProfileImage(
      (size > 50
        ? publicContact?.profile_image_large
        : publicContact?.profile_image) || undefined,
    )
  }, [publicContact, name, size])

  const [avatarSizeStyles, avatarImageSizeStyles] = useMemo(
    () => [
      {
        width: `${size}px`,
        height: `${size}px`,
      },
      {
        width: `${imageSize || size}px`,
        height: `${imageSize || size}px`,
      },
    ],
    [size, imageSize],
  )

  return (
    <span
      className={cn(
        'topic-avatar',
        avatarStyles(),
        isActiveMember && 'topic-avatar--activemember',
        className,
      )}
      title={handle}
      {...props}
    >
      {profileImage ? (
        <div
          style={avatarSizeStyles}
          className={cn('topic-avatar--image', avatarImageStyles())}
        >
          <img
            alt={initials}
            src={profileImage}
            style={avatarImageSizeStyles}
            className={imageStyles()}
            draggable={false}
          />
        </div>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={imageSize || size}
          height={imageSize || size}
          viewBox={`${xOffset} ${yOffset} ${viewBoxSize + strokeWidth} ${
            viewBoxSize + strokeWidth
          }`}
        >
          <>
            <defs>
              <linearGradient
                id={`${weakUid}-grad`}
                gradientTransform={`rotate(${gradAngle})`}
              >
                <stop
                  offset={`${
                    ((gradStartPercent - viewBoxSize) / svgSize) * 100
                  }%`}
                  stopColor={iconColors.baseColor}
                />
                <stop
                  offset={`${
                    ((gradStartPercent + viewBoxSize / 2) / svgSize) * 100
                  }%`}
                  stopColor={iconColors.baseColor2}
                />
                <stop
                  offset={`${
                    ((gradStartPercent + viewBoxSize * 2) / svgSize) * 100
                  }%`}
                  stopColor={iconColors.baseColor3}
                />
              </linearGradient>
              <mask id={`${weakUid}-br`}>
                <rect
                  width={viewBoxSize}
                  height={viewBoxSize}
                  x={xOffset + strokeWidth / 2}
                  y={yOffset + strokeWidth / 2}
                  rx={viewBoxSize / 5}
                  ry={viewBoxSize / 5}
                  fill="white"
                />
              </mask>
            </defs>
            <rect
              width={viewBoxSize}
              height={viewBoxSize}
              x={xOffset + strokeWidth / 2}
              y={yOffset + strokeWidth / 2}
              rx={viewBoxSize / 5}
              ry={viewBoxSize / 5}
              fill="white"
            />
            <path
              style={{
                fill: `url(#${weakUid}-grad)`,
                mask: `url(#${weakUid}-br)`,
              }}
              // eslint-disable-next-line
              d="M149.8,3c15.9,0.7,27.8,6.6,35.5,17.5l0,0l0.9,1.3c5.7,8.8,7.9,19.8,7.3,30.1l0,0l-0.1,1.8l1.3,0.8 l1.3,0.9c10.2,6.7,17.8,17.2,22.5,31.1l0,0l0.6,1.9l0.6,1.9l0.5,1.6l0.4,1.7c5.3,21.3,0.3,42.8-14.2,64.1l0,0l-1.5,2.1l18.5,63.3 l0.2,0.7c2.2,9.3-6.7,16.9-15,13.4l0,0l-0.7-0.3l-0.7-0.4L162.3,210l-0.8,0.5l-1.6,1c-32.6,19.4-68.1,17.5-105.1-5.3l0,0l-2-1.2 l-1.9-1.3l-2-1.4c-65.6-46-23.7-169.1,68-194.7c9.9-2.8,18.9-4.3,26.9-4.6L149.8,3L149.8,3z M166.7,33.6 c-5.9-7.7-19.2-10.2-43.6-3.3l0,0l-2.3,0.7c-74.3,22.6-106.6,121.2-58,153c17.7,11.6,34.4,17.4,50.2,17.4l0,0l1.8,0 c14-0.4,27.3-5.3,40.3-14.7l0,0l0.7-0.5c3.2-2,7.2-2.2,10.6-0.5l0,0l0.8,0.4l26.5,15.6l-12.1-41.3l-0.2-0.9 c-0.6-2.9-0.2-5.9,1.2-8.4l0,0l0.5-0.7l0.5-0.7l1.2-1.6c13.3-17.5,17.7-33.6,14-48.7l0,0l-0.4-1.4l-0.4-1.4l-0.5-1.7 c-2.6-8.1-6.3-14.1-11-18.1l0,0l-0.9,1.8c-18.2,36.6-55.5,67-91.6,73.5l0,0l-1,0.2c-14.3,2.2-25-5.7-25.2-20l0,0l0-1.3 c0.2-8.8,4.5-19.4,11.6-30.5l0,0l1.1-1.7c15.5-23.6,39.8-44,63-49.5l0,0l2.3-0.5c9-2,17.4-2.7,25.2-2l0,0l-0.1-1.5 c-0.3-3.4-1.1-6.7-2.5-9.4l0,0l-0.6-1.1l-0.6-0.9L166.7,33.6L166.7,33.6z M164.2,70.2l-0.6,0l-1.7,0.1c-3.4,0.2-7.1,0.7-11,1.6l0,0 l-2,0.5l-1.4,0.4c-17.1,4.7-36.9,21.8-49.1,41.1l0,0l-0.9,1.4c-3.1,5-5.2,9.6-6.2,13l0,0l-0.2,0.7l0.6-0.1l1.6-0.4 c26.9-6.3,55.4-29.5,70.4-57.1l0,0L164.2,70.2L164.2,70.2z"
            />
            <rect
              width={viewBoxSize}
              height={viewBoxSize}
              x={xOffset + strokeWidth / 2}
              y={yOffset + strokeWidth / 2}
              rx={viewBoxSize / 5}
              ry={viewBoxSize / 5}
              fill="transparent"
              stroke={`url(#${weakUid}-grad)`}
              strokeWidth={strokeWidth}
            />
            {presence === 'away' && !hideOnlineIndicator && (
              <circle cx="83.5%" cy="83.5%" r={5.5} fill="#ffab2d" />
            )}
            {presence === 'online' && !hideOnlineIndicator && (
              <circle cx="83.5%" cy="83.5%" r={5.5} fill="#3BC88C" />
            )}
          </>
        </svg>
      )}

      {badge != undefined && badge > 0 && (
        <Badge
          css={{
            position: 'absolute',
            top: '0',
            right: '$space$1',
            transform: 'translate(10px, -8px)',
            zIndex: 2,
          }}
        >
          {badge}
        </Badge>
      )}
    </span>
  )
}

// TODO: change this and all other occurances in the future
// this is a cursed issue. `defaultProps` is going away soon
// --- link: https://github.com/reactjs/rfcs/pull/107
// but the only proposed solutions thus far involve React.FC and aliases which is bad
// --- link: https://github.com/Microsoft/TypeScript/issues/27425
// --- link: https://github.com/microsoft/TypeScript/issues/31247
// --- link: https://github.com/facebook/create-react-app/pull/8177
// this will need time to play around with to keep the types from getting messed up
// so for now we can do this but in the future it will need to be changed. Just need to
// create an example project with a bunch of test cases to write the correct helper -- will
// do this later, _for now_ its too much time for the reward vs using this
Avatar.defaultProps = {
  hideOnlineIndicator: false,
  presence: 'offline',
  isActiveMember: false,
}

const strokeWidth = 2
